import { equal, handleAxiosError, ilike, nested } from '@/api/helpers';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { CampaignCatalogueItem, phishingSignsLabels } from '@/client/campaigns';
import { Actions, Subjects, SystemRoles } from '@/client/users';
import {
  CATALOGUE_READ_ONLY_PREVIEW_PARAM,
  LoadingStatuses,
  RedirectPaths,
  RedirectPathsEnum,
} from '@/common/constants';
import { CampaignCatalogueCardV2 } from '@/components/campaigns/CampaignCatalogueCardV2';
import { CatalogueFilterBar } from '@/components/campaigns/catalogue/CatalogueFilterBar';
import { CatalogueGrid } from '@/components/campaigns/catalogue/CatalogueGrid.styled';
import { FeaturedSimulationsSection } from '@/components/campaigns/catalogue/FeaturedSimulationsSection';
import { PreviewCampaignTemplate } from '@/components/campaigns/PreviewCampaignTemplate';
import {
  useCampaignCatalogueTemplates,
  useCloneCampaignTemplate,
  useFeaturedCampaignTemplates,
} from '@/hooks/query/campaign-templates.hook';
import { useAppSelector } from '@/hooks/store';
import { useCatalogueFilters } from '@/hooks/useCatalogueFilters';
import { usePermission } from '@/hooks/usePermission';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { AppButton } from '@/ui/buttons';
import AppChip from '@/ui/chip/AppChip';
import { Slider } from '@/ui/slider';
import { FlexContainer } from '@/ui/styled-ui';
import { isDemoModeActive } from '@/utils/demo-mode';
import { isTrialRestricted } from '@/utils/posthog';
import { AxiosError } from 'axios';
import { MenuItem } from 'primereact/menuitem';
import { Paginator, PaginatorPageChangeEvent } from 'primereact/paginator';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

const StyledContainer = styled(FlexContainer)`
  flex-direction: column;
  min-height: 100vh;
  justify-content: flex-start;
  align-items: flex-start;
`;

const StyledHeader = styled.div`
  text-align: left;
  margin: var(--small-padding) 0 var(--medium-padding) 0;

  h1 {
    font-size: var(--heading-medium-font-size);
    line-height: var(--heading-medium-line-height);
    margin: 0 0 var(--xxsmall-padding) 0;
  }

  p {
    font-size: var(--small-font-size);
    margin: 0;
    color: var(--gray-deep);
  }
`;

const StyledGridContainer = styled(CatalogueGrid)`
  min-width: calc(280px * 3 + var(--default-padding) * 2);
`;

const StyledPreviewOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 1100;
  background: var(--white-main);
  display: flex;
  flex-direction: column;
`;

const StyledPreviewHeader = styled.div`
  display: flex;
  align-items: center;
  gap: var(--small-padding);
  border-bottom: 1px solid var(--gray-light);
  padding: var(--default-padding);

  h1 {
    font-size: var(--heading-medium-font-size);
    line-height: var(--heading-medium-line-height);
    margin: 0;
  }
`;

const StyledPreviewContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0;
`;

const StyledPreviewFooter = styled.div`
  border-top: 1px solid var(--gray-dark);
  padding: var(--default-padding);
`;

const StyledAppChip = styled(AppChip)`
  cursor: pointer;
  border-bottom: 2px solid transparent;

  &:hover {
    border-color: var(--red-dark);
  }
`;

const StyledPaginatorContainer = styled(FlexContainer)`
  .p-paginator {
    .p-dropdown {
      min-height: var(--default-input-height);
      overflow: hidden;

      .p-dropdown-label {
        padding: 10px var(--default-padding);
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 40px;
        line-height: normal;
      }

      .p-dropdown-trigger {
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
  }
`;

type CampaignsCataloguePageV2Props = {
  mode?: 'create' | 'select';
  onSelect?: (template: CampaignCatalogueItem) => void;
  entryPoint?: 'direct-catalogue' | 'phishing-simulation';
};

export const CampaignsCataloguePageV2: React.FC<
  CampaignsCataloguePageV2Props
> = ({ mode = 'create', onSelect, entryPoint = 'direct-catalogue' }) => {
  const { t } = useTranslation();
  const { can } = usePermission();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);

  const isFreeTrial =
    !!currentAccount &&
    isTrialRestricted(currentAccount) &&
    [SystemRoles.ADMIN, SystemRoles.MULTIPLE_ACCOUNTS_ADMIN].includes(
      currentUser?.role?.code as SystemRoles,
    );

  const isModalContext = entryPoint === 'phishing-simulation';

  const isReadOnlyPreview =
    !isModalContext &&
    searchParams.get(CATALOGUE_READ_ONLY_PREVIEW_PARAM) === 'true';

  const { filters, updateFilter } = useCatalogueFilters();
  const [previewCampaign, setPreviewCampaign] =
    useState<CampaignCatalogueItem | null>(null);
  const [, setIsCreating] = useState<LoadingStatuses>(LoadingStatuses.IDLE);

  const catalogueFilters = useMemo(() => {
    const filterArray = [];

    // filterArray.push(equal('usageFilter', filters.usageFilter));

    if (filters.search) {
      filterArray.push(ilike('name', filters.search));
    }

    if (filters.language) {
      filterArray.push(equal('language', filters.language));
    }

    if (filters.difficulty) {
      filterArray.push(equal('difficulty', filters.difficulty));
    }

    if (filters.phishingSign) {
      filterArray.push(
        nested('meta', [equal('phishingSign', filters.phishingSign)]),
      );
    }

    return filterArray;
  }, [
    filters.search,
    filters.language,
    filters.difficulty,
    filters.phishingSign,
  ]);

  const { catalogueTemplates, isLoading } = useCampaignCatalogueTemplates(
    currentAccount?.id || '',
    {
      usageFilter: filters.usageFilter,
      filters: catalogueFilters,
      take: filters.take,
      skip: filters.skip,
      // Once per failed request, not on every re-render while the error stays.
      onError: (error) => handleAxiosError(error as Error | AxiosError, toast),
    },
  );

  const cloneCampaign = useCloneCampaignTemplate();

  const canCreateSimulation =
    can(Actions.CREATE, Subjects.CAMPAIGNS) && !isDemoModeActive();
  const canReadCampaigns = can(Actions.READ, Subjects.CAMPAIGNS);
  const {
    featuredTemplates,
    isLoading: isLoadingFeatured,
    error: featuredError,
  } = useFeaturedCampaignTemplates(currentAccount?.id || '');

  useEffect(() => {
    if (featuredError) {
      handleAxiosError(featuredError as Error | AxiosError, toast);
    }
  }, [featuredError, toast]);

  const isMasterView =
    catalogueTemplates?.view === 'master' ||
    featuredTemplates?.view === 'master';
  const hideCreateAction = mode === 'create' && isMasterView;

  const canPerformAction =
    mode === 'select' ? canReadCampaigns : canCreateSimulation;

  const shouldShowActionButton =
    !isFreeTrial &&
    !isReadOnlyPreview &&
    (mode === 'create' ? canCreateSimulation : canPerformAction);

  const findTemplate = (campaignId: string) =>
    catalogueTemplates?.items.find((template) => template.id === campaignId) ??
    featuredTemplates?.items.find((template) => template.id === campaignId);

  const handlePreview = (campaignId: string) => {
    const campaign = findTemplate(campaignId);
    if (campaign) {
      setPreviewCampaign(campaign);
    }
  };

  const handleSelect = async (campaignId: string) => {
    const template = findTemplate(campaignId);

    if (mode === 'select' && onSelect && template) {
      onSelect(template);
      return;
    }

    if (mode === 'create') {
      try {
        if (!currentAccount) return;
        setIsCreating(LoadingStatuses.LOADING);

        const response = await cloneCampaign.clone({
          campaignId,
          account: currentAccount.id,
        });

        setIsCreating(LoadingStatuses.IDLE);

        if (response?.id) {
          const editPath = RedirectPaths[RedirectPathsEnum.CAMPAIGNS_EDIT](
            response.id,
          );
          navigate(editPath, {
            state: { isPublished: false },
          });
          toast?.success(t('toast.success'), t('campaign.created'));
        }
      } catch (e) {
        handleAxiosError(e as Error | AxiosError, toast);
        setIsCreating(LoadingStatuses.IDLE);
      }
    }
  };

  const breadcrumbItems: MenuItem[] = [
    {
      label: currentAccount?.name,
      url: currentAccount?.id
        ? !currentAccount?.isSystem
          ? RedirectPaths[RedirectPathsEnum.ACCOUNT](currentAccount.id)
          : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](currentAccount.id)
        : undefined,
      template: AppBreadCrumbTemplate,
      data: { end: true },
    },
    {
      label: t('campaigns'),
      url: RedirectPaths[RedirectPathsEnum.CAMPAIGNS](),
      template: AppBreadCrumbTemplate,
      data: { end: true },
    },
    {
      label: t('campaigns.catalogue'),
      url: RedirectPaths[RedirectPathsEnum.CAMPAIGNS_CATALOGUE_V2](),
      template: AppBreadCrumbTemplate,
    },
  ];

  const footer = previewCampaign && (
    <FlexContainer width="100%" justify="flex-start" gap={24}>
      <Slider>
        <FlexContainer
          gap={8}
          justify="flex-start"
          align="flex-start"
          flex="1 1 80%"
        >
          {catalogueTemplates?.items.map((template) => (
            <StyledAppChip
              key={template.id}
              label={template.name}
              type="error"
              onClick={() => {
                const campaign = catalogueTemplates.items.find(
                  (t) => t.id === template.id,
                );
                if (campaign) setPreviewCampaign(campaign);
              }}
              style={{
                borderBottom:
                  previewCampaign.id === template.id
                    ? '2px solid var(--red-dark)'
                    : 'none',
              }}
            />
          ))}
        </FlexContainer>
      </Slider>

      <FlexContainer
        justify="flex-end"
        className="mt-auto pb-2 min-w-max"
        wrap="wrap"
        flex="1 1 15%"
      >
        <AppButton
          label={t('generic.close')}
          severity="secondary"
          type="outlined"
          onClick={() => setPreviewCampaign(null)}
          className="mr-3"
        />
        {shouldShowActionButton && !hideCreateAction && (
          <AppButton
            label={
              mode === 'create'
                ? t('campaign.template.createSimulation')
                : t('campaign.template.selectTemplate')
            }
            onClick={() => handleSelect(previewCampaign.id)}
          />
        )}
      </FlexContainer>
    </FlexContainer>
  );

  return (
    <>
      {!isModalContext && <AppBreadCrumb model={breadcrumbItems} />}

      <StyledContainer
        className={`campaign-catalogue-v2 campaign-catalogue-v2--${entryPoint}`}
      >
        {!isModalContext && (
          <StyledHeader>
            <h1>{t('campaigns.catalogue.v2.title')}</h1>
            <p>{t('campaigns.catalogue.v2.subtitle')}</p>
          </StyledHeader>
        )}

        <FeaturedSimulationsSection
          featuredTemplates={featuredTemplates}
          isLoading={isLoadingFeatured}
          mode={mode}
          canPerformAction={shouldShowActionButton}
          hideCreateAction={hideCreateAction}
          onPreview={handlePreview}
          onSelect={handleSelect}
        />

        <CatalogueFilterBar filters={filters} onFilterChange={updateFilter} />

        {isLoading ? (
          <FlexContainer justify="center" align="center" className="mt-5">
            <ProgressSpinner />
          </FlexContainer>
        ) : !catalogueTemplates?.items.length ? (
          <FlexContainer justify="center" align="center" className="mt-5">
            <h3>{t('campaign.template.email.empty')}</h3>
          </FlexContainer>
        ) : (
          <>
            <StyledGridContainer>
              {catalogueTemplates.items.map(
                (campaign: CampaignCatalogueItem) => (
                  <CampaignCatalogueCardV2
                    key={campaign.masterTemplateId ?? campaign.id}
                    campaign={campaign}
                    onPreview={handlePreview}
                    onSelect={handleSelect}
                    mode={mode}
                    canPerformAction={shouldShowActionButton}
                    hideCreateAction={hideCreateAction}
                  />
                ),
              )}
            </StyledGridContainer>

            <StyledPaginatorContainer justify="flex-end" className="mt-4">
              <Paginator
                rows={filters.take}
                first={filters.skip}
                totalRecords={catalogueTemplates.total}
                pageLinkSize={3}
                rowsPerPageOptions={[12, 24, 36, 48, 100]}
                onPageChange={(event: PaginatorPageChangeEvent) => {
                  updateFilter({ skip: event.first, take: event.rows }, false);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                template="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                currentPageReportTemplate={`{first} ${t(
                  'filter.to',
                )} {last} ${t('filter.of')} {totalRecords}`}
              />
            </StyledPaginatorContainer>
          </>
        )}
      </StyledContainer>

      {previewCampaign && (
        <StyledPreviewOverlay>
          <StyledPreviewHeader>
            {previewCampaign.phishingSign && (
              <AppChip
                label={phishingSignsLabels(t)[previewCampaign.phishingSign]}
                type={`phishing-${previewCampaign.phishingSign}` as any}
              />
            )}
            <h1>{previewCampaign.name}</h1>
          </StyledPreviewHeader>
          <StyledPreviewContent>
            <PreviewCampaignTemplate
              campaignId={previewCampaign.id}
              hideHeader={true}
            />
          </StyledPreviewContent>
          <StyledPreviewFooter>{footer}</StyledPreviewFooter>
        </StyledPreviewOverlay>
      )}
    </>
  );
};
